import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, ImageOff } from "lucide-react";
import { useAdminProducts } from "@/lib/adminProductsStore";
import type { Product } from "@/data/products";
import { cn } from "@/lib/utils";

const CATEGORIES = ["donna", "uomo", "accessori", "scarpe"];
const BADGES = ["", "SALE", "NEW", "BEST SELLER"] as const;

type FormData = Omit<Product, "id">;

const empty: FormData = {
  name: "",
  description: "",
  longDescription: "",
  price: 0,
  originalPrice: undefined,
  category: "donna",
  image: "",
  rating: 5,
  reviews: 0,
  badge: undefined,
  inStock: true,
  featured: false,
  sizes: [],
  material: "",
};

function Field({
  label, children, required, hint,
}: { label: string; children: React.ReactNode; required?: boolean; hint?: string }) {
  return (
    <div>
      <label className="block text-[10px] tracking-widest uppercase text-stone-500 font-medium mb-1.5">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      {children}
      {hint && <p className="text-xs text-stone-400 mt-1">{hint}</p>}
    </div>
  );
}

const inputClass = "w-full border border-stone-200 bg-white px-4 py-2.5 text-sm focus:outline-none focus:border-stone-600 text-stone-800 placeholder-stone-300 transition-colors";

export default function AdminProductForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { products, addProduct, updateProduct } = useAdminProducts();

  const existing = id ? products.find((p) => p.id === id) : null;
  const isEdit = !!existing;

  const [form, setForm] = useState<FormData>(
    existing
      ? {
          name: existing.name,
          description: existing.description,
          longDescription: existing.longDescription,
          price: existing.price,
          originalPrice: existing.originalPrice,
          category: existing.category,
          image: existing.image,
          rating: existing.rating,
          reviews: existing.reviews,
          badge: existing.badge,
          inStock: existing.inStock,
          featured: existing.featured ?? false,
          sizes: existing.sizes ?? [],
          material: existing.material ?? "",
        }
      : { ...empty }
  );
  const [sizesInput, setSizesInput] = useState(
    (existing?.sizes ?? []).join(", ")
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  function set<K extends keyof FormData>(key: K, value: FormData[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => { const e = { ...prev }; delete e[key]; return e; });
  }

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (!form.name.trim()) e.name = "Il nome è obbligatorio";
    if (!form.description.trim()) e.description = "La descrizione breve è obbligatoria";
    if (!form.price || form.price <= 0) e.price = "Il prezzo deve essere maggiore di 0";
    if (!form.category) e.category = "Seleziona una categoria";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);

    const sizes = sizesInput
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    const data: FormData = {
      ...form,
      sizes,
      originalPrice: form.originalPrice || undefined,
      badge: (form.badge as Product["badge"]) || undefined,
    };

    setTimeout(() => {
      if (isEdit && id) {
        updateProduct(id, data);
      } else {
        addProduct(data);
      }
      setSaving(false);
      navigate("/admin/products");
    }, 300);
  }

  return (
    <div className="max-w-4xl">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate("/admin/products")}
          className="p-2 text-stone-400 hover:text-stone-900 hover:bg-stone-200 transition-colors"
        >
          <ArrowLeft className="size-4" />
        </button>
        <div>
          <h1 className="text-2xl font-semibold text-stone-900">
            {isEdit ? "Modifica prodotto" : "Nuovo prodotto"}
          </h1>
          {isEdit && <p className="text-stone-400 text-sm mt-0.5">{existing?.name}</p>}
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main info */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white border border-stone-200 p-6 space-y-5">
              <h2 className="text-[10px] tracking-widest uppercase text-stone-500 font-semibold">Informazioni principali</h2>

              <Field label="Nome prodotto" required>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  placeholder="Es. Abito in Seta Milano"
                  className={cn(inputClass, errors.name && "border-red-400")}
                />
                {errors.name && <p className="text-red-400 text-xs mt-1">{errors.name}</p>}
              </Field>

              <Field label="Descrizione breve" required>
                <input
                  type="text"
                  value={form.description}
                  onChange={(e) => set("description", e.target.value)}
                  placeholder="Una riga, max 80 caratteri"
                  maxLength={80}
                  className={cn(inputClass, errors.description && "border-red-400")}
                />
                {errors.description && <p className="text-red-400 text-xs mt-1">{errors.description}</p>}
              </Field>

              <Field label="Descrizione estesa" hint="Mostrata nella pagina prodotto">
                <textarea
                  value={form.longDescription}
                  onChange={(e) => set("longDescription", e.target.value)}
                  placeholder="Descrivi materiali, vestibilità, caratteristiche speciali..."
                  rows={5}
                  className={cn(inputClass, "resize-none")}
                />
              </Field>

              <Field label="Materiale">
                <input
                  type="text"
                  value={form.material}
                  onChange={(e) => set("material", e.target.value)}
                  placeholder="Es. 100% Seta pura, Cashmere mongolico"
                  className={inputClass}
                />
              </Field>
            </div>

            <div className="bg-white border border-stone-200 p-6 space-y-5">
              <h2 className="text-[10px] tracking-widest uppercase text-stone-500 font-semibold">Prezzi</h2>

              <div className="grid grid-cols-2 gap-4">
                <Field label="Prezzo (€)" required>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.price || ""}
                    onChange={(e) => set("price", parseFloat(e.target.value) || 0)}
                    placeholder="Es. 890"
                    className={cn(inputClass, errors.price && "border-red-400")}
                  />
                  {errors.price && <p className="text-red-400 text-xs mt-1">{errors.price}</p>}
                </Field>

                <Field label="Prezzo originale (€)" hint="Lascia vuoto se non in saldo">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.originalPrice || ""}
                    onChange={(e) => set("originalPrice", parseFloat(e.target.value) || undefined)}
                    placeholder="Es. 1100"
                    className={inputClass}
                  />
                </Field>
              </div>
            </div>

            <div className="bg-white border border-stone-200 p-6 space-y-5">
              <h2 className="text-[10px] tracking-widest uppercase text-stone-500 font-semibold">Taglie</h2>
              <Field label="Taglie disponibili" hint="Separale con una virgola: XS, S, M, L, XL oppure 36, 37, 38">
                <input
                  type="text"
                  value={sizesInput}
                  onChange={(e) => setSizesInput(e.target.value)}
                  placeholder="XS, S, M, L, XL"
                  className={inputClass}
                />
              </Field>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Image */}
            <div className="bg-white border border-stone-200 p-6 space-y-4">
              <h2 className="text-[10px] tracking-widest uppercase text-stone-500 font-semibold">Immagine</h2>
              <div className="aspect-[3/4] bg-stone-100 overflow-hidden flex items-center justify-center">
                {form.image ? (
                  <img src={form.image} alt="" className="w-full h-full object-cover" />
                ) : (
                  <ImageOff className="size-10 text-stone-300" />
                )}
              </div>
              <Field label="URL immagine">
                <input
                  type="url"
                  value={form.image}
                  onChange={(e) => set("image", e.target.value)}
                  placeholder="https://..."
                  className={inputClass}
                />
              </Field>
            </div>

            {/* Organizzazione */}
            <div className="bg-white border border-stone-200 p-6 space-y-5">
              <h2 className="text-[10px] tracking-widest uppercase text-stone-500 font-semibold">Organizzazione</h2>

              <Field label="Categoria" required>
                <select
                  value={form.category}
                  onChange={(e) => set("category", e.target.value)}
                  className={cn(inputClass, errors.category && "border-red-400")}
                >
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                  ))}
                </select>
              </Field>

              <Field label="Badge">
                <select
                  value={form.badge ?? ""}
                  onChange={(e) => set("badge", (e.target.value as Product["badge"]) || undefined)}
                  className={inputClass}
                >
                  {BADGES.map((b) => (
                    <option key={b} value={b}>{b || "Nessuno"}</option>
                  ))}
                </select>
              </Field>

              <div className="space-y-3 pt-1">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.inStock}
                    onChange={(e) => set("inStock", e.target.checked)}
                    className="size-4 accent-stone-900"
                  />
                  <span className="text-sm text-stone-700">Disponibile in stock</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.featured}
                    onChange={(e) => set("featured", e.target.checked)}
                    className="size-4 accent-stone-900"
                  />
                  <span className="text-sm text-stone-700">In evidenza (homepage)</span>
                </label>
              </div>
            </div>

            {/* Rating */}
            <div className="bg-white border border-stone-200 p-6 space-y-4">
              <h2 className="text-[10px] tracking-widest uppercase text-stone-500 font-semibold">Recensioni</h2>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Rating">
                  <input
                    type="number"
                    min="0"
                    max="5"
                    step="0.1"
                    value={form.rating}
                    onChange={(e) => set("rating", parseFloat(e.target.value))}
                    className={inputClass}
                  />
                </Field>
                <Field label="N° recensioni">
                  <input
                    type="number"
                    min="0"
                    value={form.reviews}
                    onChange={(e) => set("reviews", parseInt(e.target.value) || 0)}
                    className={inputClass}
                  />
                </Field>
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={saving}
              className="w-full py-4 bg-stone-900 text-white text-xs tracking-widest uppercase font-semibold hover:bg-stone-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <Save className="size-4" />
              {saving ? "Salvataggio..." : isEdit ? "Salva modifiche" : "Crea prodotto"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
